import produce from 'immer';
import { ampli } from 'lib-shared-tracking';
import sortBy from 'lodash/sortBy';
import { useCallback, useMemo, useState } from 'react';
import { useDeepCompareEffect } from 'react-use';
import { DeepWritable, Writable } from 'ts-essentials';

import { UserStorageKeybindings } from 'apis/userStorage';
import {
  createPrettyKeyCombination,
  isAppleDevice,
  KeyboardKeyCombination,
  KeyboardModifierKeyCode,
  keyCombinationsToString,
} from 'utils/keyboard';
import { useServerStorage } from 'utils/useServerStorage';

import { CommandName, EnrichedCommand } from '../types/command.types';
import { commands, commandsKeyboardRebindable } from '../utils/commands';

/** Represents the result of the {@link useCommands} hook. */
export type UseCommandsResult = {
  /** The enriched commands. */
  readonly enrichedCommands: readonly EnrichedCommand[];
  /** The current keybindings of available commands. */
  readonly keybindings: Record<string, readonly KeyboardKeyCombination[]>;
  /**
   * Whether all rebindable keybindings can be reset. Returns `false` if all
   * rebindable keybindings are already reset.
   */
  readonly canResetAllRebindableKeybindings: boolean;
  /**
   * Whether all rebindable keybindings can be unassigned. Returns `false` if
   * all rebindable keybindings are already unassigned.
   */
  readonly canUnassignAllRebindableKeybindings: boolean;
  /** Whether there are keybindings conflicts. */
  readonly hasKeybindingsConflicts: boolean;
  /** The function for setting user defined keybinding of a given command. */
  readonly setKeybindings: UseCommandsSetKeybindingsFn;
  /**
   * The function for resetting user defined keybinding to the default
   * keybinding of a given command.
   */
  readonly resetKeybindings: UseCommandsResetKeybindingsFn;
  /**
   * The function for removing the keybinding of a command. This essentially
   * overrides the default keybinding with an empty one.
   */
  readonly unassignKeybindings: UseCommandsUnassignKeybindingsFn;
  /** The function for removing all keybindings of all rebindable commands. */
  readonly unassignAllKeybindings: () => void;
  /** The function for resetting all user keybindings. */
  readonly resetAllUserKeybindings: () => void;
  /** The function for getting keybindings of one or multiple commands. */
  readonly getKeybindings: UseCommandsGetKeybindingsFn;
};

/** Represents the user storage key for keybindings. */
const userStorageKey: keyof UserStorageKeybindings = isAppleDevice
  ? 'keybindings_mac'
  : 'keybindings_pc';

/** Represents a hook for working with commands. */
export function useCommands(): UseCommandsResult {
  const { data, set, remove } = useServerStorage('user');
  const [userKeybindings, setUserKeybindings] = useState<
    Partial<Record<string, readonly KeyboardKeyCombination[]>>
  >(data[userStorageKey] ?? {});

  const userKeybindingsData = data[userStorageKey] ?? {};

  // Updates the user defined keybindings local state. We do this to ensure that
  // the consumers only re-render when keybindings update and not the rest of
  // the user storage.
  useDeepCompareEffect(() => {
    setUserKeybindings(userKeybindingsData);
  }, [userKeybindingsData]);

  /** Commands enriched with user defined keybindings. */
  const commandsWithUserKeybindings = useMemo(() => {
    const sortedCommands = sortBy(commands, ['group', 'label']);

    return sortedCommands.map((command) => {
      const commandUserKeybindings = userKeybindings[command.name] as
        | readonly KeyboardKeyCombination<KeyboardModifierKeyCode>[]
        | undefined;

      if (commandUserKeybindings) {
        return produce(command, (draft) => {
          draft.keyboard = {
            ...draft.keyboard,
            keybindings: commandUserKeybindings as DeepWritable<
              typeof commandUserKeybindings
            >,
          };
        });
      }

      return command;
    });
  }, [userKeybindings]);

  /** Commands and their current keybindings. */
  const keybindings = useMemo(() => {
    return Object.fromEntries(
      commandsWithUserKeybindings.map((command) => [
        command.name,
        command.keyboard?.keybindings ?? [],
      ]),
    ) as UseCommandsResult['keybindings'];
  }, [commandsWithUserKeybindings]);

  const canResetAllRebindableKeybindings = useMemo(
    () => !!Object.keys(userKeybindings).length,
    [userKeybindings],
  );

  const canUnassignAllRebindableKeybindings = useMemo(
    () =>
      commandsKeyboardRebindable.some(
        ({ name }) => !(userKeybindings[name]?.length === 0),
      ),
    [userKeybindings],
  );

  const setKeybindings = useCallback<UseCommandsSetKeybindingsFn>(
    (commandName, newKeybindings) => {
      if (
        !commandsKeyboardRebindable.some(({ name }) => name === commandName)
      ) {
        console.warn(
          `Keybindings of '${commandName}' command can not be rebound`,
        );

        return;
      }

      const oldKeybindings = keyCombinationsToString(keybindings[commandName]);

      set({
        [userStorageKey]: (draft) => {
          draft[commandName] = newKeybindings as DeepWritable<
            typeof newKeybindings
          >;
        },
      });

      ampli.settingsCommandsAssignKeybindings({
        commandName,
        oldKeybindings,
        newKeybindings: keyCombinationsToString(newKeybindings),
      });
    },
    [keybindings, set],
  );

  const resetKeybindings = useCallback<UseCommandsResetKeybindingsFn>(
    (commandName) => {
      if (
        !commandsKeyboardRebindable.some(({ name }) => name === commandName)
      ) {
        console.warn(
          `Keybindings of '${commandName}' command can not be reset because it's not keyboard rebindable`,
        );

        return;
      }

      set({
        [userStorageKey]: (draft) => {
          draft[commandName] = null;
        },
      });

      ampli.settingsCommandsResetKeybindings({ commandName });
    },
    [set],
  );

  const unassignKeybindings = useCallback<UseCommandsUnassignKeybindingsFn>(
    (commandName) => {
      if (
        !commandsKeyboardRebindable.some(({ name }) => name === commandName)
      ) {
        console.warn(
          `Keybindings of '${commandName}' command can not be unassigned because it's not keyboard rebindable`,
        );

        return;
      }

      set({
        [userStorageKey]: (draft) => {
          draft[commandName] = [];
        },
      });

      ampli.settingsCommandsUnassignKeybindings({ commandName });
    },
    [set],
  );

  const unassignAllKeybindings = useCallback(() => {
    set({
      [userStorageKey]: (draft) => {
        commandsKeyboardRebindable.forEach(({ name }) => {
          draft[name] = [];
        });
      },
    });

    ampli.settingsCommandsUnassignAllKeybindings();
  }, [set]);

  const resetAllUserKeybindings = useCallback(() => {
    remove([userStorageKey]);

    ampli.settingsCommandsResetAllKeybindings();
  }, [remove]);

  const isCommandKeyboardRebindable = useCallback((name: string) => {
    return !!commandsKeyboardRebindable.find(
      (command) => command.name === name,
    );
  }, []);

  const isUserDefinedCommandKeybindings = useCallback(
    (name: string) => {
      return name in userKeybindings;
    },
    [userKeybindings],
  );

  const hasCommandKeybindings = useCallback(
    (name: string) => {
      return !!(keybindings[name]?.length > 0);
    },
    [keybindings],
  );

  const enrichedCommands = useMemo<readonly EnrichedCommand[]>(() => {
    const lookupItems = commandsWithUserKeybindings.reduce<
      readonly [string, CommandConflicts][]
    >((lookupItems, command) => {
      (command.keyboard?.keybindings ?? []).forEach((keybinding) => {
        const keybindingsString = createPrettyKeyCombination(keybinding, {
          preferBinding: true,
        }).toString();

        const lookupItem = lookupItems.find(
          ([keybindings]) => keybindings === keybindingsString,
        );

        const seenCommand: CommandConflicts = {
          [command.name]: {
            command,
            isUserDefinedKeybindings: isUserDefinedCommandKeybindings(
              command.name,
            ),
          },
        };

        if (lookupItem) {
          lookupItem[1] = Object.assign(lookupItem[1], seenCommand);
        } else {
          (lookupItems as Writable<typeof lookupItems>).push([
            keybindingsString,
            seenCommand,
          ]);
        }
      });

      return lookupItems;
    }, []);

    const commandConflicts = commandsWithUserKeybindings.reduce<
      Record<string, CommandConflicts>
    >((acc, command) => {
      const lookupItem = lookupItems.find(
        ([_, seenCommands]) => seenCommands[command.name],
      );

      if (lookupItem) {
        acc[command.name] = Object.fromEntries(
          Object.entries(lookupItem[1]).filter(
            ([name]) => command.name !== name,
          ),
        );
      }

      return acc;
    }, {});

    return commandsWithUserKeybindings.map((command) => {
      const { name } = command;
      const conflict = commandConflicts[name];
      const hasKeybindings = hasCommandKeybindings(name);
      const isKeyboardRebindable = isCommandKeyboardRebindable(name);
      const isUserDefinedKeybindings = isUserDefinedCommandKeybindings(name);
      const canResetKeybindings =
        isKeyboardRebindable && isUserDefinedKeybindings;
      const canUnassignKeybindings = isKeyboardRebindable && hasKeybindings;

      return {
        command,
        conflicts: conflict
          ? Object.entries(conflict).map(
              ([_, command]) => command as EnrichedCommand,
            )
          : [],
        isUserDefinedKeybindings,
        canResetKeybindings,
        canUnassignKeybindings,
        hasKeybindings,
        isKeyboardRebindable,
      };
    });
  }, [
    hasCommandKeybindings,
    isCommandKeyboardRebindable,
    isUserDefinedCommandKeybindings,
    commandsWithUserKeybindings,
  ]);

  const hasKeybindingsConflicts = useMemo(
    () => enrichedCommands.some(({ conflicts }) => conflicts.length > 0),
    [enrichedCommands],
  );

  const getKeybindings = useCallback(
    function <TCommandName extends CommandName>(
      commandNames: readonly TCommandName[],
    ): UseCommandsGetKeybindingsResult<TCommandName> {
      return Object.fromEntries(
        Object.entries(keybindings).filter(([commandName]) =>
          commandNames.includes(commandName as TCommandName),
        ),
      ) as UseCommandsGetKeybindingsResult<TCommandName>;
    },
    [keybindings],
  );

  return {
    enrichedCommands,
    keybindings,
    canResetAllRebindableKeybindings,
    canUnassignAllRebindableKeybindings,
    hasKeybindingsConflicts,
    setKeybindings,
    getKeybindings,
    resetKeybindings,
    unassignKeybindings,
    resetAllUserKeybindings,
    unassignAllKeybindings,
  };
}

/** Represents the function for setting keybindings of a given command. */
export type UseCommandsSetKeybindingsFn = (
  commandName: string,
  keybindings: readonly KeyboardKeyCombination[],
) => void;

type UseCommandsGetKeybindingsResult<TCommandName extends CommandName> = {
  [TName in TCommandName]: readonly KeyboardKeyCombination[];
};

export type UseCommandsGetKeybindingsFn<
  TCommandName extends CommandName = CommandName,
> = (
  commandNames: readonly TCommandName[],
) => UseCommandsGetKeybindingsResult<TCommandName>;

/**
 * Represents the function for resetting user defined keybinding to the default
 * keybinding of a given command.
 */
export type UseCommandsResetKeybindingsFn = (commandName: string) => void;

/** Represents the function for removing keybindings of a given command. */
export type UseCommandsUnassignKeybindingsFn = (commandName: string) => void;

/** Represents commands and their conflicts by keybindings. */
type CommandConflicts = Record<
  string,
  Pick<EnrichedCommand, 'command' | 'isUserDefinedKeybindings'>
>;
